// PDF / PNG export helpers for moodboards & project boards.
// Uses html2canvas to rasterize live DOM, and jsPDF to assemble pages.
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";

// A4 landscape mm: 297 x 210
const A4_W = 297;
const A4_H = 210;

async function snapshot(el, scale = 2) {
  return html2canvas(el, {
    backgroundColor: null,
    scale,
    useCORS: true,
    logging: false,
  });
}

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export async function exportElementToPNG(el, filename = "atelier.png") {
  const canvas = await snapshot(el, 2);
  return new Promise((resolve) => {
    canvas.toBlob((blob) => {
      if (blob) downloadBlob(blob, filename);
      resolve();
    }, "image/png");
  });
}

// Place a rendered canvas onto an A4 landscape page, fit-contain.
function addCanvasAsPage(pdf, canvas, addPage = false, padding = 8) {
  if (addPage) pdf.addPage();
  const innerW = A4_W - padding * 2;
  const innerH = A4_H - padding * 2;
  const cAspect = canvas.width / canvas.height;
  let w = innerW;
  let h = innerW / cAspect;
  if (h > innerH) {
    h = innerH;
    w = innerH * cAspect;
  }
  const x = (A4_W - w) / 2;
  const y = (A4_H - h) / 2;
  const dataUrl = canvas.toDataURL("image/jpeg", 0.92);
  pdf.addImage(dataUrl, "JPEG", x, y, w, h);
}

export async function exportElementToPDF(el, filename = "moodboard.pdf") {
  const canvas = await snapshot(el, 2);
  const pdf = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
  addCanvasAsPage(pdf, canvas, false, 8);
  pdf.save(filename);
}

// Build a hidden DOM container, render it to canvas, return canvas. Cleans up.
async function withHiddenDom(buildEl) {
  const wrap = document.createElement("div");
  wrap.style.position = "fixed";
  wrap.style.left = "-99999px";
  wrap.style.top = "0";
  wrap.style.width = "1600px";
  wrap.style.background = "#FFFFFF";
  wrap.style.fontFamily = "Inter, system-ui, sans-serif";
  document.body.appendChild(wrap);
  try {
    const el = buildEl(wrap);
    // wait one frame for images to start loading
    await new Promise((r) => setTimeout(r, 50));
    // wait for all images inside to load
    const imgs = Array.from(el.querySelectorAll("img"));
    await Promise.all(
      imgs.map(
        (img) =>
          new Promise((resolve) => {
            if (img.complete) return resolve();
            img.onload = img.onerror = () => resolve();
          })
      )
    );
    const canvas = await snapshot(el, 1.5);
    return canvas;
  } finally {
    wrap.remove();
  }
}

function coverPageHTML(project, coverSrc) {
  return `
    <div style="width:1600px;height:1000px;position:relative;overflow:hidden;background:#1C1B17;color:white;">
      ${
        coverSrc
          ? `<img src="${coverSrc}" style="position:absolute;inset:0;width:100%;height:100%;object-fit:cover;opacity:0.55;" crossOrigin="anonymous" />`
          : ""
      }
      <div style="position:absolute;inset:0;background:linear-gradient(180deg, rgba(0,0,0,0.1) 0%, rgba(0,0,0,0.7) 100%);"></div>
      <div style="position:absolute;left:90px;bottom:90px;right:90px;">
        <div style="font-size:18px;letter-spacing:0.3em;text-transform:uppercase;opacity:0.7;margin-bottom:24px;">${
          project.client ? project.client.replace(/</g, "") : "Project Board"
        }</div>
        <div style="font-family:'Cormorant Garamond', 'Times New Roman', serif;font-size:96px;line-height:1.05;margin-bottom:18px;">${(
          project.name || ""
        ).replace(/</g, "")}</div>
        <div style="font-size:20px;opacity:0.75;max-width:1100px;">${(
          project.description || ""
        ).replace(/</g, "")}</div>
        <div style="margin-top:60px;font-size:14px;letter-spacing:0.25em;text-transform:uppercase;opacity:0.6;">Atelier Studio — Presented ${new Date().toLocaleDateString(
          undefined,
          { year: "numeric", month: "long", day: "numeric" }
        )}</div>
      </div>
    </div>
  `;
}

function galleryPageHTML(title, images, columns = 3) {
  const cell = `width:${(1600 - 80 - (columns - 1) * 20) / columns}px;height:280px;object-fit:cover;border-radius:8px;background:#EFE9DF;`;
  const pdfCell = `width:${(1600 - 80 - (columns - 1) * 20) / columns}px;height:280px;border-radius:8px;background:#F4EEDF;display:flex;flex-direction:column;align-items:center;justify-content:center;font-family:'Cormorant Garamond', Georgia, serif;`;
  return `
    <div style="width:1600px;min-height:1000px;background:#FAF8F4;padding:60px 40px;">
      <div style="font-family:'Cormorant Garamond', 'Times New Roman', serif;font-size:48px;color:#1C1B17;margin-bottom:30px;">${title}</div>
      <div style="display:grid;grid-template-columns:repeat(${columns}, 1fr);gap:20px;">
        ${images
          .map((img) => {
            const isPdfItem =
              (img.mime || "").toLowerCase() === "application/pdf" ||
              /^data:application\/pdf/.test(img.data || "") ||
              (img.filename || "").toLowerCase().endsWith(".pdf");
            const media = isPdfItem
              ? `<div style="${pdfCell}">
                  <div style="font-size:14px;letter-spacing:0.25em;text-transform:uppercase;color:#1C1B17;opacity:0.6;margin-bottom:10px;">PDF</div>
                  <div style="font-size:18px;color:#1C1B17;max-width:80%;text-align:center;">${(img.filename || "Document").replace(/</g, "")}</div>
                </div>`
              : `<img src="${img.data}" style="${cell}" />`;
            return `
            <div style="display:flex;flex-direction:column;gap:6px;">
              ${media}
              <div style="font-size:12px;color:#6B6B6B;line-height:1.4;">
                ${(img.title || "").replace(/</g, "").slice(0, 80)}
                ${
                  img.tags && img.tags.length
                    ? `<div style="margin-top:2px;font-size:10px;opacity:0.7;">${img.tags
                        .slice(0, 4)
                        .map((t) => "#" + t.replace(/</g, ""))
                        .join(" ")}</div>`
                    : ""
                }
              </div>
            </div>
          `;
          })
          .join("")}
      </div>
    </div>
  `;
}

function moodboardPageHTML(mb, images) {
  const CANVAS_W = 1600;
  const CANVAS_H = 1000;
  const FONT_FAMILIES = {
    display: "'Cormorant Garamond', 'Times New Roman', serif",
    sans: "'Inter', system-ui, sans-serif",
    mono: "'JetBrains Mono', ui-monospace, monospace",
  };
  const imgMap = Object.fromEntries(images.map((i) => [i.id, i]));
  const items = [...(mb.items || [])].sort(
    (a, b) => (a.z || 0) - (b.z || 0)
  );
  return `
    <div style="width:1600px;height:1100px;background:#FAF8F4;padding:50px 50px 20px 50px;">
      <div style="display:flex;justify-content:space-between;align-items:flex-end;margin-bottom:18px;">
        <div style="font-family:'Cormorant Garamond', 'Times New Roman', serif;font-size:44px;color:#1C1B17;">${(
          mb.name || "Moodboard"
        ).replace(/</g, "")}</div>
        <div style="font-size:12px;letter-spacing:0.2em;text-transform:uppercase;color:#8A8A8A;">Moodboard</div>
      </div>
      <div style="position:relative;width:1500px;height:937.5px;background:${
        mb.background || "#F7F7F5"
      };border-radius:14px;overflow:hidden;">
        ${items
          .map((it) => {
            const left = (it.x / CANVAS_W) * 1500;
            const top = (it.y / CANVAS_H) * 937.5;
            const w = (it.w / CANVAS_W) * 1500;
            const h = (it.h / CANVAS_H) * 937.5;
            if (it.kind === "text") {
              const align = it.align || "left";
              const justify =
                align === "center" ? "center" : align === "right" ? "flex-end" : "flex-start";
              return `<div style="position:absolute;left:${left}px;top:${top}px;width:${w}px;height:${h}px;transform:rotate(${
                it.rotation || 0
              }deg);display:flex;align-items:center;justify-content:${justify};padding:0 8px;font-family:${
                FONT_FAMILIES[it.font_family] || FONT_FAMILIES.display
              };font-size:${(it.font_size || 32) * 0.6}px;color:${
                it.color || "#1c1b17"
              };text-align:${align};white-space:pre-wrap;word-break:break-word;line-height:1.1;">${(
                it.text || ""
              ).replace(/</g, "")}</div>`;
            }
            const img = imgMap[it.image_id];
            if (!img) return "";
            const isPdfItem =
              (img.mime || "").toLowerCase() === "application/pdf" ||
              /^data:application\/pdf/.test(img.data || "") ||
              (img.filename || "").toLowerCase().endsWith(".pdf");
            if (isPdfItem) {
              return `<div style="position:absolute;left:${left}px;top:${top}px;width:${w}px;height:${h}px;transform:rotate(${
                it.rotation || 0
              }deg);background:#FFFFFF;border:1px solid #E7E2D7;border-radius:6px;display:flex;flex-direction:column;align-items:center;justify-content:center;color:#1C1B17;box-shadow:0 4px 14px rgba(0,0,0,0.12);">
                <div style="font-size:11px;letter-spacing:0.25em;text-transform:uppercase;opacity:0.55;margin-bottom:6px;">PDF</div>
                <div style="font-size:14px;max-width:80%;text-align:center;font-family:'Cormorant Garamond', serif;">${(img.filename || "Document").replace(/</g, "")}</div>
              </div>`;
            }
            return `<img src="${img.data}" style="position:absolute;left:${left}px;top:${top}px;width:${w}px;height:${h}px;object-fit:cover;border-radius:6px;transform:rotate(${
              it.rotation || 0
            }deg);box-shadow:0 4px 14px rgba(0,0,0,0.12);" />`;
          })
          .join("")}
      </div>
    </div>
  `;
}

function floorplanPageHTML(fp) {
  return `
    <div style="width:1600px;min-height:1000px;background:#FAF8F4;padding:60px 40px;">
      <div style="display:flex;justify-content:space-between;align-items:flex-end;margin-bottom:18px;">
        <div style="font-family:'Cormorant Garamond', 'Times New Roman', serif;font-size:44px;color:#1C1B17;">${(
          fp.name || "Floorplan"
        ).replace(/</g, "")}</div>
        <div style="font-size:12px;letter-spacing:0.2em;text-transform:uppercase;color:#8A8A8A;">Floorplan${
          fp.pins && fp.pins.length ? ` — ${fp.pins.length} pins` : ""
        }</div>
      </div>
      <div style="position:relative;width:1520px;height:880px;background:#FFFFFF;border-radius:10px;overflow:hidden;border:1px solid #E7E2D7;">
        ${
          fp.image_data
            ? `<img src="${fp.image_data}" style="position:absolute;inset:0;width:100%;height:100%;object-fit:contain;" />`
            : ""
        }
        ${(fp.pins || [])
          .map((p, idx) => {
            const left = (p.x || 0) * 1520;
            const top = (p.y || 0) * 880;
            return `<div style="position:absolute;left:${left}px;top:${top}px;transform:translate(-50%,-100%);">
              <div style="background:#1C1B17;color:white;border-radius:999px;padding:4px 10px;font-size:12px;font-weight:600;">${
                idx + 1
              }</div>
            </div>`;
          })
          .join("")}
      </div>
      ${
        fp.pins && fp.pins.length > 0
          ? `<div style="margin-top:16px;display:grid;grid-template-columns:repeat(2,1fr);gap:10px 30px;font-size:13px;color:#3F3F3F;">
              ${fp.pins
                .map(
                  (p, idx) =>
                    `<div><strong style="color:#1C1B17;">${idx + 1}.</strong> ${(
                      p.note ||
                      p.label ||
                      "(no note)"
                    ).replace(/</g, "")}</div>`
                )
                .join("")}
            </div>`
          : ""
      }
    </div>
  `;
}

function productsPageHTML(products) {
  const fmt = (v) => (typeof v === "number" ? `$${v.toLocaleString()}` : v || "—");
  const rows = products
    .map(
      (p) => `
      <tr style="border-bottom:1px solid #EAE5D9;">
        <td style="padding:14px 10px;font-size:14px;color:#1C1B17;">${(p.name || "").replace(/</g, "")}</td>
        <td style="padding:14px 10px;font-size:13px;color:#6B6B6B;">${(p.vendor || "—").replace(/</g, "")}</td>
        <td style="padding:14px 10px;font-size:13px;color:#6B6B6B;">${(p.sku || "—").replace(/</g, "")}</td>
        <td style="padding:14px 10px;font-size:13px;color:#1C1B17;text-align:right;">${fmt(p.price)}</td>
      </tr>`
    )
    .join("");
  return `
    <div style="width:1600px;min-height:1000px;background:#FAF8F4;padding:60px 40px;">
      <div style="font-family:'Cormorant Garamond', 'Times New Roman', serif;font-size:48px;color:#1C1B17;margin-bottom:30px;">Products</div>
      <table style="width:100%;border-collapse:collapse;background:white;border-radius:8px;overflow:hidden;">
        <thead>
          <tr style="background:#F4EEDF;">
            <th style="text-align:left;padding:14px 10px;font-size:11px;letter-spacing:0.18em;text-transform:uppercase;color:#6B6B6B;">Name</th>
            <th style="text-align:left;padding:14px 10px;font-size:11px;letter-spacing:0.18em;text-transform:uppercase;color:#6B6B6B;">Vendor</th>
            <th style="text-align:left;padding:14px 10px;font-size:11px;letter-spacing:0.18em;text-transform:uppercase;color:#6B6B6B;">SKU</th>
            <th style="text-align:right;padding:14px 10px;font-size:11px;letter-spacing:0.18em;text-transform:uppercase;color:#6B6B6B;">Price</th>
          </tr>
        </thead>
        <tbody>${rows}</tbody>
      </table>
    </div>
  `;
}

// Export a single moodboard as PDF with cover.
export async function exportMoodboardPDF(mb, images, project) {
  const pdf = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
  const cover = await withHiddenDom((wrap) => {
    const html = coverPageHTML(
      { name: mb.name, client: project?.name || "", description: "" },
      images.find((i) => i.id === (mb.items || [])[0]?.image_id)?.data
    );
    wrap.innerHTML = html;
    return wrap.firstElementChild;
  });
  addCanvasAsPage(pdf, cover, false, 0);

  const board = await withHiddenDom((wrap) => {
    wrap.innerHTML = moodboardPageHTML(mb, images);
    return wrap.firstElementChild;
  });
  addCanvasAsPage(pdf, board, true, 0);

  pdf.save(`${(mb.name || "moodboard").replace(/\s+/g, "_")}.pdf`);
}

// Export the whole project as a presentation PDF.
// sections: array subset of ["cover","library","moodboards","floorplans","products"]
export async function exportProjectPDF({
  project,
  images,
  moodboards,
  floorplans = [],
  products = [],
  sections,
}) {
  const include = sections
    ? new Set(sections)
    : new Set(["cover", "library", "moodboards"]);
  const pdf = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
  let firstPageUsed = false;

  function placeCanvas(canvas) {
    addCanvasAsPage(pdf, canvas, firstPageUsed, 0);
    firstPageUsed = true;
  }

  // 1. Cover
  if (include.has("cover")) {
    const cover = await withHiddenDom((wrap) => {
      wrap.innerHTML = coverPageHTML(project, project.cover_image);
      return wrap.firstElementChild;
    });
    placeCanvas(cover);
  }

  // 2. Library gallery, 6 per page (2 rows x 3 cols)
  if (include.has("library") && images && images.length > 0) {
    const perPage = 6;
    for (let i = 0; i < images.length; i += perPage) {
      const slice = images.slice(i, i + perPage);
      const pageNum = Math.floor(i / perPage) + 1;
      const totalPages = Math.ceil(images.length / perPage);
      const page = await withHiddenDom((wrap) => {
        wrap.innerHTML = galleryPageHTML(
          `Library ${totalPages > 1 ? `${pageNum} / ${totalPages}` : ""}`,
          slice,
          3
        );
        return wrap.firstElementChild;
      });
      placeCanvas(page);
    }
  }

  // 3. One page per moodboard
  if (include.has("moodboards") && moodboards) {
    for (const mb of moodboards) {
      const page = await withHiddenDom((wrap) => {
        wrap.innerHTML = moodboardPageHTML(mb, images || []);
        return wrap.firstElementChild;
      });
      placeCanvas(page);
    }
  }

  // 4. Floorplans
  if (include.has("floorplans") && floorplans) {
    for (const fp of floorplans) {
      const page = await withHiddenDom((wrap) => {
        wrap.innerHTML = floorplanPageHTML(fp);
        return wrap.firstElementChild;
      });
      placeCanvas(page);
    }
  }

  // 5. Products
  if (include.has("products") && products && products.length > 0) {
    const perPage = 14;
    for (let i = 0; i < products.length; i += perPage) {
      const slice = products.slice(i, i + perPage);
      const page = await withHiddenDom((wrap) => {
        wrap.innerHTML = productsPageHTML(slice);
        return wrap.firstElementChild;
      });
      placeCanvas(page);
    }
  }

  // Fallback: if nothing was rendered, drop a blank cover so file isn't empty.
  if (!firstPageUsed) {
    const cover = await withHiddenDom((wrap) => {
      wrap.innerHTML = coverPageHTML(project, project.cover_image);
      return wrap.firstElementChild;
    });
    placeCanvas(cover);
  }

  pdf.save(`${(project.name || "project").replace(/\s+/g, "_")}_board.pdf`);
}
